/* eslint-disable max-lines-per-function */
/* eslint-disable max-statements */
import { Kitchen3dController as TdComponentController, RENDERING_CAPTURE_ASPECT } from '@rta/rta-blueprint-3d-component';
import {
	AspectRatio,
	Box,
	Button,
	Collapse,
	Flex,
	FormLabel,
	HStack,
	Image,
	Input,
	Radio,
	RadioGroup,
	Spinner,
	Stack,
	Text,
	useDisclosure
} from '@chakra-ui/react';
import { useCallback, useEffect, useState, type JSX } from 'react';
import { ChevronDown, ChevronRight, Download } from 'lucide-react';

import { ContextPanelContent } from './context-panel-content';
import { OutdoorPhotosPicker, RenderingPreviewSurface, RenderingUploadZone, type PreviewCapture } from '@components/ui';
import { getRendering } from '@external';
import { lang } from '@lang';
import {
	initFromMetadata,
	projectState,
	selectAiRendering,
	selectCanonical,
	selectCustomerName,
	selectRawRendering,
	setCanonical,
	useAppDispatch,
	useAppSelector
} from '@state';
import { useDesign, useSetRendering } from '@hooks';
import { useKeystoneSession } from '../../../auth/use-keystone-session';

export const RenderingPropertiesPanel = (): JSX.Element => {
	const [, design] = useDesign();
	const controller = (design?.tdController ?? null) as TdComponentController | null;
	const dispatch = useAppDispatch();
	const { getToken } = useKeystoneSession();
	const { setRendering, isSaving: isSavingRendering } = useSetRendering();

	const rawBase64 = useAppSelector(selectRawRendering);
	const aiBase64 = useAppSelector(selectAiRendering);
	const canonical = useAppSelector(selectCanonical);
	const customerName = useAppSelector(selectCustomerName);
	const project = useAppSelector(projectState);

	const kitchenDealId = controller?.kitchen?.dealId ?? '';
	const deal = project.find((d) => d.dealId === kitchenDealId) ?? project?.[0];
	const dealId = deal?.dealId ?? null;

	const blueprint = controller?.kitchen?.context?.blueprint ?? null;
	const metadata = blueprint?.metadata ?? null;

	// Seed slice from blueprint metadata once per design — keyed on design identity, NOT the
	// mutable path strings, so a fresh capture never re-triggers a re-seed that wipes dirty state.
	useEffect(() => {
		if (!blueprint) return;
		dispatch(initFromMetadata({
			rawPath: blueprint.thumbnail || metadata?.renderingRawPath || '',
			aiPath: metadata?.renderingAiPath ?? '',
			canonical: metadata?.renderingCanonical ?? 'raw',
			signature: metadata?.renderingKitchenSignature ?? ''
		}));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [blueprint?.id, dispatch]);

	// Screenshot capture — writes the rendering immediately via useSetRendering (in-memory + API)
	const [isCapturing, setIsCapturing] = useState(false);
	const [capture, setCapture] = useState<PreviewCapture | null>(null);
	const handleSetAsRendering = useCallback(async (): Promise<void> => {
		if (!capture || !controller) return;
		setIsCapturing(true);
		try {
			const raw = await capture(2);
			if (raw) await setRendering({ image: raw, kitchen: controller.kitchen });
		} finally {
			setIsCapturing(false);
		}
	}, [capture, controller, setRendering]);

	// Canonical selector — re-runs the shared write path so switching raw/ai persists immediately too
	const handleCanonicalChange = useCallback((val: string): void => {
		const src = val as 'raw' | 'ai';
		const chosen = src === 'ai' ? aiBase64 : rawBase64;
		if (chosen) void setRendering({ image: chosen, kitchen: controller?.kitchen ?? null, source: src });
		else dispatch(setCanonical(src));
	}, [controller, dispatch, setRendering, aiBase64, rawBase64]);

	// AI generation state
	const [isGenerating, setIsGenerating] = useState(false);
	const [generateError, setGenerateError] = useState<string | null>(null);
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [isDragOver, setIsDragOver] = useState(false);
	const [downloadVisible, setDownloadVisible] = useState(false);
	const { isOpen: aiOpen, onToggle: toggleAi } = useDisclosure({ defaultIsOpen: false });

	const handleDragOver = useCallback((e: React.DragEvent): void => {
		e.preventDefault();
		setIsDragOver(true);
	}, []);

	const handleDragLeave = useCallback((e: React.DragEvent): void => {
		e.preventDefault();
		setIsDragOver(false);
	}, []);

	const handleDrop = useCallback((e: React.DragEvent): void => {
		e.preventDefault();
		setIsDragOver(false);
		const { files } = e.dataTransfer;
		if (files.length > 0) setSelectedFile(files[0]);
	}, []);

	const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>): void => {
		const { files } = e.target;
		if (files && files.length > 0) setSelectedFile(files[0]);
	}, []);

	const handlePhotoSelect = useCallback((base64: string): void => {
		fetch(base64)
			.then((res) => res.blob())
			.then((blob) => {
				setSelectedFile(new File([blob], 'outdoor-photo.jpg', { type: 'image/jpeg' }));
			});
	}, []);

	const handleGenerate = useCallback(async (): Promise<void> => {
		if (!selectedFile) return;
		setIsGenerating(true);
		setGenerateError(null);

		const reader = new FileReader();
		reader.readAsDataURL(selectedFile);
		reader.onload = async (e) => {
			const base64String = e.target?.result as string;
			const [rendering, error] = await getRendering({
				image: base64String,
				prompt: 'Add a small amount of gentle shadows around the base of the outdoor kitchen.',
				n: '1',
				output_format: 'png',
				quality: 'high',
				response_format: 'b64_json',
				size: '1536x1024',
				getToken
			});
			if (error) {
				setGenerateError(error);
			} else if (rendering) {
				const full = rendering.startsWith('data:') ? rendering : `data:image/png;base64,${rendering}`;
				await setRendering({ image: full, kitchen: controller?.kitchen ?? null, source: 'ai' });
			}
			setIsGenerating(false);
		};
	}, [selectedFile, getToken, setRendering]);

	const handleDownload = useCallback((src: string): void => {
		const img = new window.Image();
		img.onload = () => {
			const canvas = document.createElement('canvas');
			canvas.width = img.naturalWidth;
			canvas.height = img.naturalHeight;
			const ctx = canvas.getContext('2d');
			if (!ctx) return;
			ctx.fillStyle = '#FFFFFF';
			ctx.fillRect(0, 0, canvas.width, canvas.height);
			ctx.drawImage(img, 0, 0);
			canvas.toBlob((blob) => {
				if (!blob) return;
				const url = URL.createObjectURL(blob);
				const link = document.createElement('a');
				link.href = url;
				link.download = `rendering_${customerName}.png`;
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				setTimeout(() => URL.revokeObjectURL(url), 100);
			}, 'image/png', 1.0);
		};
		img.src = src;
	}, [customerName]);

	if (!controller) return <></>;

	const canCapture = !!capture;

	return <ContextPanelContent title={lang.contextPanels.title.renderingProperties}>
		<Stack spacing="3" width="100%" pb="10px">

			{/* Live preview */}
			<Box position="relative">
				<FormLabel fontSize="small">Live 3D Preview</FormLabel>
				<RenderingPreviewSurface
					tdController={controller}
					active={true}
					onCaptureReady={(fn) => setCapture(() => fn)}
				/>
				<Button
					mt={2}
					size="sm"
					width="100%"
					isLoading={isCapturing || isSavingRendering}
					isDisabled={!canCapture || isCapturing || isSavingRendering}
					onClick={handleSetAsRendering}
				>
					Set As Rendering
				</Button>
			</Box>

			{/* Saved renderings */}
			{(rawBase64 || aiBase64) && (
				<Box>
					<FormLabel fontSize="small">Saved Renderings</FormLabel>
					<RadioGroup value={canonical} onChange={handleCanonicalChange}>
						<Stack spacing={2}>
							{rawBase64 && (
								<Box position="relative">
									<Box
										borderRadius="md"
										overflow="hidden"
										border="2px solid"
										borderColor={canonical === 'raw' ? 'blue.400' : 'gray.200'}
										onMouseEnter={() => setDownloadVisible(true)}
										onMouseLeave={() => setDownloadVisible(false)}
									>
										<AspectRatio ratio={RENDERING_CAPTURE_ASPECT} w="100%" bg="gray.50">
											<Box><Image src={rawBase64} alt="Raw screenshot" w="100%" h="100%" objectFit="contain" /></Box>
										</AspectRatio>
										{downloadVisible && (
											<Box position="absolute" top={1} right={1}>
												<Button size="xs" variant="solid" colorScheme="gray" opacity={0.85} onClick={() => handleDownload(rawBase64)}>
													<Download size={12} />
												</Button>
											</Box>
										)}
									</Box>
									<HStack mt={1}>
										<Radio value="raw" size="sm"><Text fontSize="xs">Raw screenshot</Text></Radio>
									</HStack>
								</Box>
							)}
							{aiBase64 && (
								<Box position="relative">
									<Box
										borderRadius="md"
										overflow="hidden"
										border="2px solid"
										borderColor={canonical === 'ai' ? 'blue.400' : 'gray.200'}
										onMouseEnter={() => setDownloadVisible(true)}
										onMouseLeave={() => setDownloadVisible(false)}
									>
										{/* AI edits come back 3:2, so this pillarboxes inside the capture frame rather than hiding the difference */}
										<AspectRatio ratio={RENDERING_CAPTURE_ASPECT} w="100%" bg="gray.50">
											<Box><Image src={aiBase64} alt="AI enhanced" w="100%" h="100%" objectFit="contain" /></Box>
										</AspectRatio>
										{downloadVisible && (
											<Box position="absolute" top={1} right={1}>
												<Button size="xs" variant="solid" colorScheme="gray" opacity={0.85} onClick={() => handleDownload(aiBase64)}>
													<Download size={12} />
												</Button>
											</Box>
										)}
									</Box>
									<HStack mt={1}>
										<Radio value="ai" size="sm"><Text fontSize="xs">AI enhanced</Text></Radio>
									</HStack>
								</Box>
							)}
						</Stack>
					</RadioGroup>
				</Box>
			)}

			{/* Upload an image to use as the rendering */}
			<RenderingUploadZone kitchen={controller?.kitchen ?? null} hasExistingRendering={Boolean(rawBase64 || aiBase64)} />

			{/* AI Enhance collapsible */}
			<Box borderTop="1px solid" borderColor="gray.200" pt={2}>
				<Flex
					align="center"
					cursor="pointer"
					onClick={toggleAi}
					userSelect="none"
					gap={1}
				>
					{aiOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
					<Text fontSize="sm" fontWeight="medium">AI Enhance</Text>
				</Flex>
				<Collapse in={aiOpen} animateOpacity>
					<Stack spacing={2} mt={2}>
						<Box>
							<FormLabel htmlFor="aiInputFile" fontSize="small">Input Image:</FormLabel>
							<Box
								border="2px dashed"
								borderColor={isDragOver ? 'blue.400' : 'gray.300'}
								borderRadius="md"
								p={3}
								textAlign="center"
								cursor="pointer"
								bg={isDragOver ? 'blue.50' : 'gray.50'}
								onDragOver={handleDragOver}
								onDragLeave={handleDragLeave}
								onDrop={handleDrop}
								onClick={() => document.getElementById('aiInputFile')?.click()}
							>
								<Input
									id="aiInputFile"
									type="file"
									accept="image/jpeg,image/png"
									onChange={handleFileChange}
									style={{ display: 'none' }}
								/>
								{selectedFile
									? <Image src={URL.createObjectURL(selectedFile)} objectFit="contain" maxH="100px" w="100%" />
									: <Text fontSize="xs" color="gray.500">Drag & drop or click to browse</Text>
								}
							</Box>
						</Box>
						<OutdoorPhotosPicker dealId={dealId} onSelect={handlePhotoSelect} />
						<Button
							size="sm"
							isLoading={isGenerating}
							isDisabled={isGenerating || !selectedFile}
							onClick={handleGenerate}
						>
							Generate
						</Button>
						{generateError && <Text fontSize="xs" color="red.500">{generateError}</Text>}
						{isGenerating && (
							<Flex justify="center" align="center" h="80px">
								<Spinner />
							</Flex>
						)}
					</Stack>
				</Collapse>
			</Box>

		</Stack>
	</ContextPanelContent>;
};
